import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  type ChangeEvent,
  type DragEvent,
} from 'react';

export interface ImageUploadProps {
  /** 업로드 함수 (부모에서 주입) */
  onUpload: (file: File) => Promise<{ url: string }>;
  /** 업로드 완료 시 콜백 */
  onComplete?: (url: string) => void;
  /** 에러 발생 시 콜백 */
  onError?: (error: string) => void;
  /** 파일 유효성 검사 함수 */
  validateFile?: (file: File) => { valid: boolean; error?: string };
  /** 허용 파일 타입 */
  accept?: string;
  /** 여러 파일 허용 */
  multiple?: boolean;
  /** 비활성화 */
  disabled?: boolean;
  /** 추가 클래스 */
  className?: string;
  /** 현재 이미지 URL (미리보기용) */
  currentImage?: string;
  /** 이미지 제거 콜백 */
  onRemove?: () => void;
  /** 힌트 텍스트 */
  hint?: string;
}

export const ImageUpload = forwardRef<HTMLDivElement, ImageUploadProps>(
  (
    {
      onUpload,
      onComplete,
      onError,
      validateFile,
      accept = 'image/jpeg,image/png,image/gif,image/webp',
      multiple = false,
      disabled = false,
      className = '',
      currentImage,
      onRemove,
      hint,
    },
    ref
  ) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [preview, setPreview] = useState<string | null>(currentImage || null);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFile = useCallback(
      async (file: File) => {
        // 유효성 검사
        if (validateFile) {
          const validation = validateFile(file);
          if (!validation.valid) {
            setError(validation.error || '유효하지 않은 파일입니다.');
            onError?.(validation.error || '유효하지 않은 파일입니다.');
            return;
          }
        }

        // 미리보기 생성
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        // 업로드 시작
        setIsUploading(true);
        setError(null);
        setProgress(0);

        try {
          const result = await onUpload(file);
          setProgress(100);
          onComplete?.(result.url);
        } catch (uploadError) {
          const errorMessage =
            uploadError instanceof Error
              ? uploadError.message
              : '업로드 중 오류가 발생했습니다.';
          setError(errorMessage);
          onError?.(errorMessage);
          setPreview(null);
        } finally {
          setIsUploading(false);
        }
      },
      [onUpload, onComplete, onError, validateFile]
    );

    const handleFileChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
          if (multiple) {
            Array.from(files).forEach(handleFile);
          } else {
            handleFile(files[0]);
          }
        }
        // input 리셋
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      },
      [handleFile, multiple]
    );

    const handleDragEnter = useCallback((e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          if (multiple) {
            Array.from(files).forEach(handleFile);
          } else {
            handleFile(files[0]);
          }
        }
      },
      [handleFile, disabled, multiple]
    );

    const handleClick = useCallback(() => {
      if (!disabled && !isUploading) {
        inputRef.current?.click();
      }
    }, [disabled, isUploading]);

    const handleRemove = useCallback(() => {
      setPreview(null);
      setError(null);
      setProgress(0);
      onRemove?.();
    }, [onRemove]);

    return (
      <div ref={ref} className={className}>
        {/* 미리보기가 있을 때 */}
        {preview && !isUploading ? (
          <div className="relative rounded-xl overflow-hidden border border-text/10 bg-card-bg">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-text/60 to-transparent opacity-0 hover:opacity-100 transition-opacity">
              <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                <button
                  type="button"
                  onClick={handleClick}
                  className="flex-1 px-4 py-2 bg-white text-text font-kr text-sm rounded-lg hover:bg-white/90 transition-colors"
                >
                  변경
                </button>
                {onRemove && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="px-4 py-2 bg-red-500 text-white font-kr text-sm rounded-lg hover:bg-red-600 transition-colors"
                  >
                    삭제
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* 드래그 앤 드롭 영역 */
          <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`
              relative flex flex-col items-center justify-center
              w-full h-48 rounded-xl border-2 border-dashed
              transition-all cursor-pointer
              ${
                isDragging
                  ? 'border-accent bg-accent/5'
                  : 'border-text/20 hover:border-accent/50 hover:bg-text/5'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${isUploading ? 'pointer-events-none' : ''}
            `}
          >
            {isUploading ? (
              /* 업로드 진행 중 */
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 mb-4">
                  <svg
                    className="w-full h-full text-accent animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
                <p className="font-kr text-sm text-text/60">
                  업로드 중... {Math.round(progress)}%
                </p>
                <div className="w-48 h-2 mt-3 bg-text/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ) : (
              /* 기본 상태 */
              <>
                <div className="w-12 h-12 mb-3 text-text/40">
                  <UploadIcon />
                </div>
                <p className="font-kr text-sm text-text/80 mb-1">
                  {isDragging ? '파일을 놓으세요' : '클릭하거나 파일을 드래그하세요'}
                </p>
                <p className="font-kr text-xs text-text/50">
                  {hint || 'JPG, PNG, GIF, WebP (최대 10MB)'}
                </p>
              </>
            )}
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <p className="mt-2 font-kr text-sm text-red-500">{error}</p>
        )}

        {/* 숨겨진 파일 입력 */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />
      </div>
    );
  }
);

ImageUpload.displayName = 'ImageUpload';

function UploadIcon() {
  return (
    <svg
      className="w-full h-full"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

// 여러 이미지 업로드 컴포넌트
export interface MultiImageUploadProps {
  /** 업로드 함수 */
  onUpload: (file: File) => Promise<{ url: string }>;
  /** 현재 이미지 URL 목록 */
  images: string[];
  /** 이미지 목록 변경 콜백 */
  onChange: (images: string[]) => void;
  /** 파일 유효성 검사 함수 */
  validateFile?: (file: File) => { valid: boolean; error?: string };
  /** 최대 이미지 수 */
  maxImages?: number;
  /** 비활성화 */
  disabled?: boolean;
  /** 추가 클래스 */
  className?: string;
}

export function MultiImageUpload({
  onUpload,
  images,
  onChange,
  validateFile,
  maxImages = 10,
  disabled = false,
  className = '',
}: MultiImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = useCallback(
    async (file: File): Promise<{ url: string }> => {
      setIsUploading(true);
      try {
        return await onUpload(file);
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload]
  );

  const handleComplete = useCallback(
    (url: string) => {
      if (images.length < maxImages) {
        onChange([...images, url]);
      }
    },
    [images, maxImages, onChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(images.filter((_, i) => i !== index));
    },
    [images, onChange]
  );

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const newImages = [...images];
      const [moved] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, moved);
      onChange(newImages);
    },
    [images, onChange]
  );

  return (
    <div className={className}>
      {/* 기존 이미지 그리드 */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {images.map((url, index) => (
            <div
              key={url}
              className="relative aspect-square rounded-lg overflow-hidden border border-text/10 group"
            >
              <img
                src={url}
                alt={`Image ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-text/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleReorder(index, index - 1)}
                    className="p-2 bg-white rounded-full text-text hover:bg-white/90"
                    title="앞으로 이동"
                  >
                    <ArrowLeftIcon />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600"
                  title="삭제"
                >
                  <TrashIcon />
                </button>
                {index < images.length - 1 && (
                  <button
                    type="button"
                    onClick={() => handleReorder(index, index + 1)}
                    className="p-2 bg-white rounded-full text-text hover:bg-white/90"
                    title="뒤로 이동"
                  >
                    <ArrowRightIcon />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 업로드 영역 */}
      {images.length < maxImages && (
        <ImageUpload
          onUpload={handleUpload}
          onComplete={handleComplete}
          validateFile={validateFile}
          disabled={disabled || isUploading}
          hint={`최대 ${maxImages}개 이미지 (${images.length}/${maxImages})`}
        />
      )}
    </div>
  );
}

// 아이콘 컴포넌트
function ArrowLeftIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );
}
